import { z } from "zod";
import { Json } from "../common/index.ts";
import { CustomerId } from "../fsm/fsm.schema.ts";

export const NotificationId = z.string().uuid().brand<"NotificationId">();
export type NotificationId = z.infer<typeof NotificationId>;

/**
 * Generic notification event — content pre-rendered at insert time.
 * The event_type is a namespaced string (e.g. 'fsm.transition', 'location.assigned').
 * source_entity_type / source_entity_id are loose references — no hard FK.
 */
export const NotificationDto = z.object({
  id: NotificationId,
  customerId: CustomerId,
  userId: z.string(), // auth_profiles.id (text, not UUID-branded)
  eventType: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  linkPath: z.string().nullable(),
  sourceEntityType: z.string().nullable(),
  sourceEntityId: z.string().nullable(),
  metadata: Json,
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type NotificationDto = z.infer<typeof NotificationDto>;

export const NotificationListDto = z.object({
  notifications: z.array(NotificationDto),
  unreadCount: z.number().int().min(0),
});
export type NotificationListDto = z.infer<typeof NotificationListDto>;

export const MarkNotificationReadInput = z.object({
  id: NotificationId,
});
export type MarkNotificationReadInput = z.infer<
  typeof MarkNotificationReadInput
>;

/** Known notification event types — used for preference filtering. */
export const NotificationEventType = z.enum(["fsm.transition", "fsm.created"]);
export type NotificationEventType = z.infer<typeof NotificationEventType>;

export const NOTIFICATION_EVENT_TYPES = NotificationEventType.options;

export const NotificationPreferenceDto = z.object({
  eventType: NotificationEventType,
  enabled: z.boolean(),
});
export type NotificationPreferenceDto = z.infer<
  typeof NotificationPreferenceDto
>;

export const UpdateNotificationPreferencesInput = z.object({
  preferences: z.array(NotificationPreferenceDto).min(1),
});
export type UpdateNotificationPreferencesInput = z.infer<
  typeof UpdateNotificationPreferencesInput
>;

export const PushSubscriptionInput = z.object({
  endpoint: z.string().url(),
  p256dh: z.string().min(1),
  authKey: z.string().min(1),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionInput>;

export const ChannelLanguage = z.enum(["en", "es"]);
export type ChannelLanguage = z.infer<typeof ChannelLanguage>;

export const DiscordChannelConfig = z.object({
  type: z.literal("discord"),
  channelRef: z.string().min(1),
  language: ChannelLanguage.default("es"),
});
export type DiscordChannelConfig = z.infer<typeof DiscordChannelConfig>;

export const EmailChannelConfig = z.object({
  type: z.literal("email"),
  channelRef: z.string().min(1),
  language: ChannelLanguage.default("en"),
});
export type EmailChannelConfig = z.infer<typeof EmailChannelConfig>;

export const ChannelConfig = z.discriminatedUnion("type", [
  DiscordChannelConfig,
  EmailChannelConfig,
]);
export type ChannelConfig = z.infer<typeof ChannelConfig>;

export const NotificationChannelId = z
  .string()
  .uuid()
  .brand<"NotificationChannelId">();
export type NotificationChannelId = z.infer<typeof NotificationChannelId>;

export const NotificationChannelDto = z.object({
  id: NotificationChannelId,
  customerId: z.string().uuid(),
  customerName: z.string(),
  channelType: z.string(),
  label: z.string(),
  eventTypes: z.array(z.string()),
  notifyEntityIds: z.array(z.string().uuid()),
  config: z.record(z.string(), z.unknown()),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NotificationChannelDto = z.infer<typeof NotificationChannelDto>;

export const NotificationChannelListDto = z.object({
  channels: z.array(NotificationChannelDto),
});
export type NotificationChannelListDto = z.infer<
  typeof NotificationChannelListDto
>;

export const CreateNotificationChannelInput = z.object({
  customerId: CustomerId,
  label: z.string().min(1),
  eventTypes: z.array(z.string()).min(1),
  notifyEntityIds: z.array(z.string().uuid()).default([]),
  config: ChannelConfig,
  isActive: z.boolean().default(true),
});
export type CreateNotificationChannelInput = z.infer<
  typeof CreateNotificationChannelInput
>;

export const UpdateNotificationChannelInput = z.object({
  label: z.string().min(1).optional(),
  eventTypes: z.array(z.string()).min(1).optional(),
  notifyEntityIds: z.array(z.string().uuid()).optional(),
  config: ChannelConfig.optional(),
  isActive: z.boolean().optional(),
});
export type UpdateNotificationChannelInput = z.infer<
  typeof UpdateNotificationChannelInput
>;

export const NotificationChannelRoleDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type NotificationChannelRoleDto = z.infer<
  typeof NotificationChannelRoleDto
>;

export const NotificationChannelRoleListDto = z.object({
  roles: z.array(NotificationChannelRoleDto),
});
export type NotificationChannelRoleListDto = z.infer<
  typeof NotificationChannelRoleListDto
>;

export const ChannelEnvStatusDto = z.object({
  configured: z.boolean(),
});

export const ChannelTestResultDto = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
});
export type ChannelTestResultDto = z.infer<typeof ChannelTestResultDto>;

export const TestChannelInput = z.object({
  recipientEmail: z.string().email().optional(),
});
export type TestChannelInput = z.infer<typeof TestChannelInput>;
export type ChannelEnvStatusDto = z.infer<typeof ChannelEnvStatusDto>;
